import * as Yup from 'yup';
import { useMemo, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Unstable_Grid2';
import Autocomplete from '@mui/material/Autocomplete';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedEmployees } from 'src/redux/slices/employees';

import { RHFTextField, RHFRadioGroup } from 'src/components/hook-form';

import { EmployeeStatus, IEmployeeListItem } from 'src/types/employee';
import {
  NIC_PATTERN,
  PHONE_PATTERN,
  LoanBorrowerType,
  ILoanEmployeeRef,
  LOAN_BORROWER_TYPE_OPTIONS,
} from 'src/types/loan-agreement';

export interface LoanBorrowerFormValues {
  borrowerType: LoanBorrowerType;
  employee: string | null;
  borrowerName: string;
  nicNumber: string;
  phoneNumber: string;
}

export const LOAN_BORROWER_DEFAULTS: LoanBorrowerFormValues = {
  borrowerType: LoanBorrowerType.EMPLOYEE,
  employee: null,
  borrowerName: '',
  nicNumber: '',
  phoneNumber: '',
};

const compact = (value?: string) => (value ?? '').replace(/[\s-]/g, '');

export const loanBorrowerSchemaShape = {
  borrowerType: Yup.mixed<LoanBorrowerType>().oneOf(Object.values(LoanBorrowerType)).required(),
  employee: Yup.string()
    .nullable()
    .when('borrowerType', ([borrowerType], schema) =>
      borrowerType === LoanBorrowerType.EMPLOYEE
        ? schema.required('Select an employee')
        : schema.notRequired()
    ),
  borrowerName: Yup.string().when('borrowerType', ([borrowerType], schema) =>
    borrowerType === LoanBorrowerType.OTHER
      ? schema.trim().required('Name is required')
      : schema.notRequired()
  ),
  nicNumber: Yup.string().test(
    'nic',
    'NIC must be 9 digits followed by V or X, or 12 digits',
    (value) => !value?.trim() || NIC_PATTERN.test(value.trim())
  ),
  phoneNumber: Yup.string().test(
    'phone',
    'Enter a valid Sri Lankan phone number',
    (value) => !compact(value) || PHONE_PATTERN.test(compact(value))
  ),
};

type EmployeeOption = Pick<
  IEmployeeListItem,
  '_id' | 'firstName' | 'lastName' | 'employeeNumber' | 'nicNumber'
>;

const employeeLabel = (employee: EmployeeOption) =>
  `${employee.firstName}${employee.lastName ? ` ${employee.lastName}` : ''} (${
    employee.employeeNumber
  })`;

type Props = {
  // Borrower type is fixed once the agreement exists.
  showTypeSelector?: boolean;
  // When editing: the agreement's current employee, kept selectable even if
  // they're no longer active.
  currentEmployee?: ILoanEmployeeRef;
};

/**
 * Employee picker (active employees only) or name + optional NIC/phone.
 * Stays editable even when the rest of the form is blocked by a bad-debt
 * match, so the user can correct the person or add a NIC/phone.
 */
export default function LoanBorrowerFields({ showTypeSelector = true, currentEmployee }: Props) {
  const dispatch = useAppDispatcher();

  const { control, watch } = useFormContext();

  const borrowerType: LoanBorrowerType = watch('borrowerType');

  const { employees } = useAppSelector((state) => state.employees);
  const activeEmployees: IEmployeeListItem[] = employees.employees;

  const employeeOptions: EmployeeOption[] = useMemo(
    () =>
      currentEmployee && !activeEmployees.some((option) => option._id === currentEmployee._id)
        ? [currentEmployee, ...activeEmployees]
        : activeEmployees,
    [activeEmployees, currentEmployee]
  );

  // Fetched once and searched client-side, same as the dispatch forms.
  useEffect(() => {
    dispatch(
      getPaginatedEmployees({
        page: null,
        limit: null,
        generalSearch: null,
        sortBy: 'firstName',
        sort: 'asc',
        status: EmployeeStatus.ACTIVE,
      })
    );
  }, [dispatch]);

  return (
    <Grid container spacing={2}>
      {showTypeSelector && (
        <Grid xs={12}>
          <RHFRadioGroup row name="borrowerType" options={LOAN_BORROWER_TYPE_OPTIONS} />
        </Grid>
      )}

      {borrowerType === LoanBorrowerType.EMPLOYEE && (
        <Grid xs={12} md={6}>
          <Controller
            name="employee"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                options={employeeOptions}
                value={employeeOptions.find((option) => option._id === field.value) ?? null}
                onChange={(_, option) => field.onChange(option?._id ?? null)}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                getOptionLabel={employeeLabel}
                filterOptions={(options, { inputValue }) => {
                  const query = inputValue.trim().toLowerCase();
                  return options.filter((option) =>
                    [option.firstName, option.lastName, option.employeeNumber, option.nicNumber]
                      .filter(Boolean)
                      .some((value) => String(value).toLowerCase().includes(query))
                  );
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    {employeeLabel(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    required
                    error={!!error}
                    helperText={error?.message ?? 'Only active employees can be selected'}
                  />
                )}
              />
            )}
          />
        </Grid>
      )}

      {borrowerType === LoanBorrowerType.OTHER && (
        <>
          <Grid xs={12} md={4}>
            <RHFTextField name="borrowerName" label="Name" required />
          </Grid>
          <Grid xs={12} md={4}>
            <RHFTextField
              name="nicNumber"
              label="NIC Number (Optional)"
              placeholder="e.g. 901234567V or 199012345678"
            />
          </Grid>
          <Grid xs={12} md={4}>
            <RHFTextField
              name="phoneNumber"
              label="Phone Number (Optional)"
              placeholder="e.g. 0771234567"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
